import { css } from '@emotion/react';
import {
  createColor, hexColorWithOpacityToString,
} from '~/_shared/components/colorPicker/colorPicker.helpers';
import type { Theme } from '~/_shared/themes/theme.model';
import {
  getDarkOrLightDependingOnBaseColor, getSuitableBorderColor, makeColorDarker, makeColorLighter,
} from '~/_shared/utils/colors/colors.helpers';

const ARROW_HEIGHT = 20;
export const ITEM_SIZE = 23;
const MAX_FONT_SIZE = ITEM_SIZE - 7;
const MIN_FONT_SIZE = 4;
const MAX_OVERLAY_WIDTH = 40;

export const getFontColor = (color: string) => {
  const lightTextColor = createColor('#fff');
  const darkTextColor = createColor('#000');
  const itemColor = createColor(color);

  return getDarkOrLightDependingOnBaseColor(itemColor, darkTextColor, lightTextColor).hex;
};

export const centerStyle = css({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
});

export const overlappingBackgroundStyle = (theme: Theme) => css({
  backgroundColor: hexColorWithOpacityToString(theme.backgroundColors.secondary, 0.6),
  borderRadius: 10,
  display: 'flex',
  flexDirection: 'column',
  height: 'auto',
  maxHeight: '100%',
  maxWidth: MAX_OVERLAY_WIDTH,
  overflow: 'hidden',
  padding: '2px 0',
  position: 'relative',
  width: '100%',
});

export const contentStyle = ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column' as const as 'column',
  gap: 8,
  justifyContent: 'center',
  padding: '6px 0',
});

export const arrowsWrapperStyle = css(centerStyle, {
  flexDirection: 'column',
  padding: '4px 0',
  width: '100%',
});

export const arrowStyle = (disabled?: boolean) => (theme: Theme) => css(centerStyle, {
  color: disabled ? theme.textColors.disabled : theme.textColors.secondary,
  fontSize: 32,
  height: ARROW_HEIGHT,
  width: '100%',

  '&:hover': {
    color: disabled ? theme.textColors.disabled : theme.textColors.info,
  },
});

export const dividerWrapperStyle = css({
  padding: '2px 4px',
  position: 'relative',
});

export const dividerStyle = (theme: Theme) => css({
  backgroundColor: theme.textColors.secondary,
  marginTop: -1,
});

export const getBrighterColor = (color: string) => {
  const itemColor = createColor(color);
  const lighterColor = makeColorLighter(color, 15);
  const darkerColor = makeColorDarker(color, 10);

  return getDarkOrLightDependingOnBaseColor(itemColor, darkerColor, lighterColor);
};

export const itemStyle = (color: string, active: boolean, labelLength: number) => (theme: Theme) => {
  const itemColor = createColor(color);
  const brighterColor = getBrighterColor(color);
  const fontSize = Math.max(MAX_FONT_SIZE - (labelLength - 1) * 2, MIN_FONT_SIZE);

  return css(centerStyle, {
    border: `2px solid ${active ? theme.lineColors.contrastGreen : itemColor.hex}`,
    fontSize,
    overflow: 'hidden',
    '&:hover': {
      backgroundColor: brighterColor.hex,
      borderColor: active ? theme.lineColors.green : getSuitableBorderColor(brighterColor).hex,
    },
  });
};

const fadeCommon = (visible: boolean) => css({
  height: `${ARROW_HEIGHT}px`,
  left: 0,
  opacity: visible ? 1 : 0,
  pointerEvents: 'none',
  position: 'absolute',
  right: 0,
  zIndex: 1,
  transition: 'opacity 0.3s ease',
});

const getGradient = (direction: 'to bottom' | 'to top', theme: Theme) => css({
  background: `linear-gradient(${direction}, ${hexColorWithOpacityToString(theme.backgroundColors.secondary, 0.8)}, transparent)`,
});

export const fadeTopStyle = (visible: boolean) => (theme: Theme) => css([
  fadeCommon(visible),
  getGradient('to bottom', theme),
  { top: 0 },
]);

export const fadeBottomStyle = (visible: boolean) => (theme: Theme) => css([
  fadeCommon(visible),
  getGradient('to top', theme),
  { bottom: 0 },
]);
